"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Environment, getCurrentEnv } from "../utils/Environment";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlag, useFeatureFlags } from "../context/FeatureFlagContext";
import { ThemeToggle } from "./ThemeToggle";
import { apiFetch } from "../utils/apiFetch";
import { useAuthenticatedUser } from "../hooks/useAuthenticatedUser";

const SUPPORT_URL =
  "https://teams.microsoft.com/l/channel/19%3AfKg2EDy7i77zxby-x9QGpMKkWWfMbmAT_NA8fREmdgE1%40thread.tacv2/Support?groupId=f6b7801b-88e0-4f9f-9748-b95fdd97e578&tenantId=ea80952e-a476-42d4-aaf4-5457852b0f7e";

export const Navbar = () => {
  const pathname = usePathname();
  const [envLabel, setEnvLabel] = useState(getCurrentEnv());
  const isProdEnvironment = envLabel === Environment.PROD;
  const { isLoaded } = useFeatureFlags();
  const selfOnboardingNavbarEnabled = useFeatureFlag(
    "self-onboarding-navbar-enabled",
  );
  const { user } = useAuth();
  const { hasRole } = useAuthenticatedUser();
  const showProjects = !isProdEnvironment && hasRole(['Mcp.Retrieve']);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    apiFetch("/api/config")
      .then((res) => res.json())
      .then((data) => {
        const env = data.environment?.toUpperCase();
        if (env === "UAT") setEnvLabel(Environment.UAT);
        else if (env === "PROD") setEnvLabel(Environment.PROD);
        else if (env === "DEV") setEnvLabel(Environment.DEV);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="topbar">
      <div className="topbar-left">
        <Link
          href="/"
          className="brand"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Image
            className="brand-logo"
            src="/bp_logo.png"
            alt="bp"
            width={22}
            height={22}
          />
          <span>Nexus</span>
          <span className="brand-product">Intelligence</span>
        </Link>
        <div className="nav-links">
          <Link
            href="/"
            className={`nav-link ${pathname === "/" ? "active" : ""}`}
          >
            Assistants
          </Link>
          {isLoaded && selfOnboardingNavbarEnabled && (
            <Link
              href="/onboard"
              className={`nav-link ${pathname === "/onboard" ? "active" : ""}`}
            >
              Onboard
            </Link>
          )}
          {showProjects && (
            <Link
              href="/projects"
              className={`nav-link ${pathname === "/projects" ? "active" : ""}`}
            >
              Projects
            </Link>
          )}
        </div>
      </div>

      <div className="topbar-right">
        <ThemeToggle />
        <a
          className="icon-btn"
          href={SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="Get help or report issues"
        >
          <svg className="ic" viewBox="0 0 16 16">
            <circle cx="8" cy="8" r="6" />
            <path d="M6.2 6.2a1.9 1.9 0 1 1 2.6 1.8c-.5.3-.8.6-.8 1.2M8 11.5v.01" />
          </svg>
          Support
        </a>
        {!isProdEnvironment && <span className="env-tag">{envLabel}</span>}
        <div style={{ position: "relative" }} ref={userMenuRef}>
          <button
            className="user-chip"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            title={user?.username}
            aria-label="Account"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </button>
          {userMenuOpen && (
            <div
              className="absolute right-0 mt-2 min-w-[200px] rounded-md py-2 px-3 z-50"
              style={{
                background: "var(--bg)",
                border: "1px solid var(--border)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              }}
            >
              <span style={{ fontSize: 12.5, color: "var(--fg-secondary)" }}>
                {user?.username}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
